<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useTasksStore } from 'dashboard/stores/tasks';
import { useNoteComposerState } from 'dashboard/composables/useNoteComposerState';
import { isToday, isYesterday, format } from 'date-fns';
import TimelineAPI from 'dashboard/api/timeline';
import { resolveTimelineFieldNames } from 'dashboard/helper/timelineFieldResolvers';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import TaskFormDialog from 'dashboard/components-next/Tasks/TaskFormDialog.vue';
import TimelineItem from './TimelineItem.vue';
import TimelineNoteAttachment from './TimelineNoteAttachment.vue';

// Generico - usado na pagina dedicada de Contato, Empresa e Negociacao
// (spec 127 v4 + v7). Historico (eventos de auditoria) + nota (com
// anexo/emoji/audio) misturados numa unica lista cronologica.
const props = defineProps({
  subjectType: { type: String, required: true },
  subjectId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const pipelinesStore = usePipelinesStore();
const tasksStore = useTasksStore();

const isLoading = ref(true);
const hasLoadedOnce = ref(false);
const events = ref([]);
const fieldNames = ref({ company_id: new Map(), contact_id: new Map() });
const noteContent = ref('');
const isSubmittingNote = ref(false);
const pendingFiles = ref([]);
const showEmojiPicker = ref(false);
const { isRecordingNoteAudio: isRecordingAudio } = useNoteComposerState();
const audioRecorderRef = ref(null);
const fileInputRef = ref(null);

// Toggle Nota/Tarefa acima do composer (referencia Kommo CRM, spec 127
// v8) - "Tarefa" reaproveita o TaskFormDialog.vue da spec 19 inteiro
// (due date/responsavel/validacao), so prefilado com o titulo digitado
// aqui e o vinculo com o Contato/Empresa/Negociacao desta pagina.
const composerMode = ref('note');
const taskFormDialogRef = ref(null);
const isCreatingTask = computed(() => tasksStore.uiFlags.creatingItem);

const taskLinkProps = computed(() => {
  if (props.subjectType === 'Company') {
    return { initialCompanyId: Number(props.subjectId) };
  }
  if (props.subjectType === 'Deal') {
    return { initialDealId: Number(props.subjectId) };
  }
  return { initialContactId: Number(props.subjectId) };
});

const getAllNotesBySubject = useMapGetter('notes/getAllNotesBySubject');
const notes = computed(() =>
  getAllNotesBySubject.value(props.subjectType, props.subjectId)
);

const resolveCompanyName = id => fieldNames.value.company_id.get(id) ?? id;
const resolveContactName = id => fieldNames.value.contact_id.get(id) ?? id;

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const [timelineResponse] = await Promise.all([
      TimelineAPI.get(props.subjectType, props.subjectId),
      store.dispatch('notes/get', {
        subjectType: props.subjectType,
        subjectId: props.subjectId,
      }),
    ]);
    events.value = timelineResponse.data;
    fieldNames.value = await resolveTimelineFieldNames(events.value);
  } finally {
    isLoading.value = false;
    hasLoadedOnce.value = true;
  }
};

watch(
  () => [props.subjectType, props.subjectId],
  () => {
    fetchAll();
    if (!pipelinesStore.getPipelinesList.length) pipelinesStore.get();
  },
  { immediate: true }
);

const dayLabel = date => {
  if (isToday(date)) return t('CONTACT_DETAIL.HISTORY.TODAY');
  if (isYesterday(date)) return t('CONTACT_DETAIL.HISTORY.YESTERDAY');
  return format(date, 'dd/MM/yyyy');
};

const entries = computed(() => {
  const eventEntries = events.value.map(event => ({
    kind: 'event',
    key: `event-${event.id}`,
    timestampMs: new Date(event.created_at).getTime(),
    raw: event,
  }));
  const noteEntries = notes.value.map(note => ({
    kind: 'note',
    key: `note-${note.id}`,
    timestampMs: note.createdAt * 1000,
    content: note.content,
    userName: note.user?.name || null,
    files: note.files || [],
  }));
  return [...eventEntries, ...noteEntries];
});

// Lista plana (sem agrupar por tipo nem precisar de "Expandir") - so
// separada em dias, igual ao painel de Empresa/Negociacao. Agrupar por
// tipo escondia informacao e bagunçava a ordem cronologica quando uma
// nota acontecia no meio de varias alteracoes de campo.
const groupedByDay = computed(() => {
  const dayMap = new Map();

  entries.value.forEach(entry => {
    const date = new Date(entry.timestampMs);
    const dayKey = format(date, 'yyyy-MM-dd');
    if (!dayMap.has(dayKey)) {
      dayMap.set(dayKey, { dayKey, label: dayLabel(date), items: [] });
    }
    dayMap.get(dayKey).items.push(entry);
  });

  return Array.from(dayMap.values())
    .sort((a, b) => (a.dayKey < b.dayKey ? 1 : -1))
    .map(day => ({
      dayKey: day.dayKey,
      label: day.label,
      items: [...day.items].sort((a, b) => b.timestampMs - a.timestampMs),
    }));
});

const hasEntries = computed(() => entries.value.length > 0);
// So mostra o spinner de tela cheia no primeiro carregamento - nas
// atualizacoes seguintes o historico atual continua visivel enquanto
// busca por tras, sem piscar preto/spinner a cada refresh.
const showFullSpinner = computed(() => isLoading.value && !hasLoadedOnce.value);

const hasPendingContent = computed(
  () => noteContent.value.trim().length > 0 || pendingFiles.value.length > 0
);

const handleEmojiClick = emoji => {
  noteContent.value += emoji;
};

const closeEmojiPicker = () => {
  showEmojiPicker.value = false;
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileInputChange = event => {
  pendingFiles.value.push(...Array.from(event.target.files || []));
  event.target.value = '';
};

const removePendingFile = index => {
  pendingFiles.value.splice(index, 1);
};

const toggleAudioRecording = () => {
  isRecordingAudio.value = !isRecordingAudio.value;
};

const handleFinishRecord = ({ file }) => {
  pendingFiles.value.push(file);
  isRecordingAudio.value = false;
};

const handleRecordError = () => {
  isRecordingAudio.value = false;
  useAlert(t('CONTACT_DETAIL.HISTORY.AUDIO_ERROR'));
};

const handleAddNote = async () => {
  if (!hasPendingContent.value) return;

  isSubmittingNote.value = true;
  try {
    await store.dispatch('notes/create', {
      subjectType: props.subjectType,
      subjectId: props.subjectId,
      content: noteContent.value.trim(),
      files: pendingFiles.value,
    });
    noteContent.value = '';
    pendingFiles.value = [];
    showEmojiPicker.value = false;
  } catch {
    useAlert(t('CONTACT_DETAIL.HISTORY.NOTE_ERROR'));
  } finally {
    isSubmittingNote.value = false;
  }
};

const openTaskDialog = () => {
  taskFormDialogRef.value?.openDialog();
};

const handleCreateTask = async taskData => {
  try {
    await tasksStore.create(taskData);
    taskFormDialogRef.value?.onSuccess();
    useAlert(t('TASKS.CREATE.MESSAGES.SUCCESS'));
    noteContent.value = '';
    composerMode.value = 'note';
  } catch {
    useAlert(t('TASKS.CREATE.MESSAGES.ERROR'));
  }
};

const handleComposerSubmit = () => {
  if (composerMode.value === 'task') {
    openTaskDialog();
    return;
  }
  handleAddNote();
};

onUnmounted(() => {
  // Evita que o Copilot fique preso na posicao "gravando audio" se o
  // usuario sair da pagina no meio de uma gravacao.
  isRecordingAudio.value = false;
});

defineExpose({ refetch: fetchAll });
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="thin-scrollbar flex-1 overflow-y-auto p-6 flex flex-col gap-6">
      <div v-if="showFullSpinner" class="flex justify-center py-6">
        <SpinnerLoader :size="24" class="text-n-blue-9" />
      </div>
      <p
        v-else-if="!hasEntries"
        class="text-sm text-n-slate-11 text-center py-4"
      >
        {{ t('TIMELINE.EMPTY') }}
      </p>
      <TransitionGroup
        v-else
        tag="div"
        class="flex flex-col gap-6"
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
      >
        <div
          v-for="day in groupedByDay"
          :key="day.dayKey"
          class="flex flex-col gap-4"
        >
          <div class="flex justify-center">
            <span
              class="px-3 py-1 rounded-full bg-n-alpha-2 text-n-slate-11 text-xs font-medium"
            >
              {{ day.label }}
            </span>
          </div>
          <TransitionGroup
            tag="div"
            class="flex flex-col gap-4"
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
          >
            <template v-for="item in day.items" :key="item.key">
              <div v-if="item.kind === 'event'" class="flex flex-col gap-4">
                <TimelineItem
                  :event="item.raw"
                  :resolve-company-name="resolveCompanyName"
                  :resolve-contact-name="resolveContactName"
                />
              </div>
              <div v-else class="flex justify-center">
                <div
                  class="px-3 py-2 rounded-xl bg-n-blue-3 text-n-slate-12 text-sm max-w-[90%] flex flex-col gap-2"
                >
                  <div v-if="item.content">
                    <span v-if="item.userName" class="font-medium">
                      {{ item.userName }}:
                    </span>
                    {{ item.content }}
                  </div>
                  <span v-else-if="item.userName" class="font-medium">
                    {{ item.userName }}
                  </span>
                  <div v-if="item.files.length" class="flex flex-col gap-2">
                    <TimelineNoteAttachment
                      v-for="file in item.files"
                      :key="file.id"
                      :file="file"
                    />
                  </div>
                </div>
              </div>
            </template>
          </TransitionGroup>
        </div>
      </TransitionGroup>
    </div>

    <div
      class="relative border-t border-n-weak p-4 shrink-0 flex flex-col gap-2"
    >
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="px-2 py-0.5 rounded-md text-xs font-medium"
          :class="
            composerMode === 'note'
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="composerMode = 'note'"
        >
          {{ t('CONTACT_DETAIL.HISTORY.COMPOSER_MODE_NOTE') }}
        </button>
        <button
          type="button"
          class="px-2 py-0.5 rounded-md text-xs font-medium"
          :class="
            composerMode === 'task'
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="composerMode = 'task'"
        >
          {{ t('CONTACT_DETAIL.HISTORY.COMPOSER_MODE_TASK') }}
        </button>
      </div>

      <div v-if="pendingFiles.length" class="flex flex-wrap gap-2">
        <div
          v-for="(file, index) in pendingFiles"
          :key="`${file.name}-${index}`"
          class="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-n-alpha-2 text-xs text-n-slate-12"
        >
          <span class="truncate max-w-[140px]">{{ file.name }}</span>
          <button
            type="button"
            class="i-lucide-x text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="removePendingFile(index)"
          />
        </div>
      </div>

      <div
        v-if="isRecordingAudio"
        class="flex items-center gap-2 rounded-lg border border-n-weak px-2"
      >
        <AudioRecorder
          ref="audioRecorderRef"
          class="flex-1"
          :audio-record-format="AUDIO_FORMATS.WAV"
          @finish-record="handleFinishRecord"
          @record-error="handleRecordError"
        />
        <Button
          icon="i-lucide-square"
          variant="ghost"
          color="ruby"
          size="sm"
          @click="audioRecorderRef?.stopRecording()"
        />
      </div>

      <div class="flex gap-2 items-center">
        <template v-if="composerMode === 'note'">
          <div v-on-click-outside="closeEmojiPicker">
            <Button
              icon="i-lucide-smile"
              variant="ghost"
              color="slate"
              size="sm"
              @click="showEmojiPicker = !showEmojiPicker"
            />
            <EmojiInput
              v-if="showEmojiPicker"
              :on-click="handleEmojiClick"
              class="!top-[unset] !right-[unset] !bottom-full !left-0 !mb-2"
            />
          </div>
          <Button
            icon="i-lucide-paperclip"
            variant="ghost"
            color="slate"
            size="sm"
            @click="triggerFileInput"
          />
          <Button
            v-if="!isRecordingAudio"
            icon="i-lucide-mic"
            variant="ghost"
            color="slate"
            size="sm"
            @click="toggleAudioRecording"
          />
          <input
            ref="fileInputRef"
            type="file"
            multiple
            class="hidden"
            accept="application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.7z,.odt,image/*,video/*,audio/*"
            @change="handleFileInputChange"
          />
        </template>
        <input
          v-model="noteContent"
          type="text"
          class="flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm"
          :placeholder="
            t(
              composerMode === 'task'
                ? 'CONTACT_DETAIL.HISTORY.TASK_PLACEHOLDER'
                : 'CONTACT_DETAIL.HISTORY.NOTE_PLACEHOLDER'
            )
          "
          @keydown.enter="handleComposerSubmit"
        />
        <Button
          :label="
            t(
              composerMode === 'task'
                ? 'CONTACT_DETAIL.HISTORY.CREATE_TASK'
                : 'CONTACT_DETAIL.HISTORY.NOTE_SUBMIT'
            )
          "
          size="sm"
          :is-loading="
            composerMode === 'task' ? isCreatingTask : isSubmittingNote
          "
          :disabled="
            composerMode === 'task'
              ? isCreatingTask
              : !hasPendingContent || isSubmittingNote
          "
          @click="handleComposerSubmit"
        />
      </div>
    </div>

    <TaskFormDialog
      ref="taskFormDialogRef"
      v-bind="taskLinkProps"
      :initial-title="noteContent"
      :is-loading="isCreatingTask"
      @create="handleCreateTask"
    />
  </div>
</template>
